import { h } from '../lib/dom.js';
import { getState, setSilent, fmtNum, fmtReporting, toReporting, totalUSDFor, CONFIG, explorerTxUrl, swapDeliverAvailable } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  setMoveField, setTransferCcy, onMoveContinue, onMoveBack, onMoveConfirm, onMoveDone,
  validateMoveAmountBlur, openOpenAccount, requestMoveLimitOverride, NATIVE_CCY, setDeliverCcy
} from '../actions.js';
import { renderActivityRail } from './activityRail.js';
// Currency-conversion "deliver as" (feature-swap.md Phase 6) — reuses
// Phase 5's pure decisions rather than reimplementing them (spec's own
// "mostly reusing Phase 4's shared logic, not new resolution code").
import { deliverOptionsFor, swapFeeDisplay } from './send.js';

// Same visually-hidden-native-checkbox + lucide square/square-check icon
// pattern as send.js's own copy (itself matching landing.js's onboarding
// checkboxes) — duplicated locally rather than shared/exported, see
// send.js's customCheckbox comment for why.
function customCheckbox(checked, onchange) {
  return h('span', { class: 'custom-checkbox-wrap' }, [
    h('input', { type: 'checkbox', class: 'custom-checkbox-input', checked, onchange }),
    h('span', { class: 'custom-checkbox-icon', 'aria-hidden': 'true', html: iconMarkup(checked ? 'squareCheck' : 'square', { size: 20, color: checked ? '#4D4DFF' : '#757993' }) })
  ]);
}

function balanceBox(w) {
  const rows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', { class: 'balance-row' }, [
      h('span', {}, `${ccy}  ${fmtNum(amt)}`),
      h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt)))
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      h('span', { class: 'name' }, w.name),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    h('div', { class: 'owner' }, w.owner || '—'),
    h('div', { class: 'balances' }, rows)
  ]);
}

function renderForm(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  const fromCcyOptions = Object.keys(curAccount.balances);
  if (!fromCcyOptions.includes(s.reportingCurrency)) fromCcyOptions.push(s.reportingCurrency);
  const availableAmt = m.ccy === s.reportingCurrency ? totalUSDFor(curAccount) : (curAccount.balances[m.ccy] || 0);
  const availableFmt = m.ccy === s.reportingCurrency ? fmtReporting(availableAmt) : fmtNum(availableAmt) + ' ' + m.ccy;

  // The reporting currency (e.g. USD) isn't a chain asset — there's no
  // network to pick for it, unlike Send's From-account ccy select this one
  // always presets a real crypto ccy on entry, so this only matters if the
  // user actively picks the reporting currency here.
  const isChainAsset = m.ccy !== s.reportingCurrency;

  const toOptions = s.wallets.filter((w) => w.id !== m.fromAccountId).map((w) => ({ value: 'account:' + w.id, label: w.name }));

  const toSelect = h('select', {
    onchange: (e) => {
      const v = e.target.value;
      if (v === '__addaccount__') openOpenAccount();
      else setMoveField('toKey', v);
    }
  }, [
    h('option', { value: '', selected: m.toKey === '' || undefined }, t('transfer.selectDestination')),
    ...toOptions.map((o) => h('option', { value: o.value, selected: o.value === m.toKey || undefined }, o.label)),
    h('option', { value: '__addaccount__' }, t('transfer.addAccount'))
  ]);

  const ccySelect = h('select', { class: 'ccy-select', onchange: (e) => setTransferCcy(e.target.value) },
    fromCcyOptions.map((c) => h('option', { value: c, selected: c === m.ccy || undefined }, c)));

  const chainOptions = CONFIG.CHAINS[m.ccy] || (m.chain ? [m.chain] : []);
  const chainSelect = h('select', { onchange: (e) => setMoveField('chain', e.target.value) },
    chainOptions.map((c) => h('option', { value: c, selected: c === m.chain || undefined }, c)));

  // Deliberately gated on CONFIG.NET[m.ccy] (via deliverOptionsFor), not on
  // m.chain — Transfer's chainSelect above has never actually affected
  // which chain a transfer executes on (a real, pre-existing, unrelated
  // bug found while building this feature, see DECISIONS.md/
  // feature-outstanding.md), so gating deliver-as on it would let a user
  // set chainSelect to Ethereum, see deliver-as offered, and have it
  // silently attempt a swap against a Tron-resolved transfer. This is why
  // Phase 6's originally-planned "constrain chainSelect to Ethereum when
  // deliver-as is active" is moot (feature-swap.md) — there's nothing to
  // constrain the selector away from.
  const deliverOptions = deliverOptionsFor(m.ccy, s.reportingCurrency, s.testnetEnabled);

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, t('transfer.title')),
    balanceBox(curAccount),
    h('div', {}, [
      h('div', { class: 'card-label' }, t('transfer.to')),
      h('div', { class: 'field-row' }, [toSelect]),
      h('div', { class: 'field-error' }, m.errors.toKey || '')
    ]),
    h('div', {}, [
      h('div', { class: 'card-label' }, t('transfer.amount')),
      h('div', { class: 'field-row' }, [
        h('input', {
          placeholder: '0.00', value: m.amount,
          oninput: (e) => setSilent((st) => ({ move: { ...st.move, amount: e.target.value, errors: { ...st.move.errors, amount: null, limit: null } } })),
          onblur: validateMoveAmountBlur
        }),
        ccySelect
      ]),
      h('div', { class: 'field-help' }, t('transfer.available', { amount: availableFmt })),
      curAccount.sendLimit != null
        ? h('div', { class: 'field-help' }, t('transfer.sendLimit', { limit: fmtReporting(curAccount.sendLimit) }))
        : null,
      h('div', { class: 'field-error' }, m.errors.amount || m.errors.limit || ''),
      m.errors.limit
        ? h('button', { class: 'btn btn-secondary btn-sm', onclick: requestMoveLimitOverride }, t('transfer.overrideLimit'))
        : null
    ]),
    isChainAsset ? h('div', {}, [
      h('div', { class: 'card-label' }, t('transfer.chain')),
      chainSelect
    ]) : null,
    // Currency-conversion "deliver as" (feature-swap.md) — not shown at
    // all for a reporting-currency amount entry, same as Send's identical
    // block.
    isChainAsset
      ? h('div', {}, [
          h('div', { class: 'card-label' }, t('transfer.deliverAs')),
          deliverOptions.length
            ? h('select', { onchange: (e) => setDeliverCcy(e.target.value) }, [
                h('option', { value: '', selected: m.deliverCcy === '' || undefined }, t('transfer.deliverAsSame')),
                ...deliverOptions.map((c) => h('option', { value: c, selected: c === m.deliverCcy || undefined }, c))
              ])
            : h('div', { class: 'field-help' }, t('transfer.deliverAsUnavailable')),
          m.errors.deliverCcy ? h('div', { class: 'field-error' }, m.errors.deliverCcy) : null
        ])
      : null,
    // No fee estimate here — every real transfer costs real gas now (item
    // 11), not just cross-currency/cross-chain ones, so a form-step
    // heuristic would either be wrong or need a live quote on every
    // keystroke. The real quote appears one tap later, on review — the
    // actual signing-confirmation screen CLAUDE.md's fee requirement is
    // about.
    h('button', { class: 'btn btn-primary', onclick: onMoveContinue }, t('transfer.continueToReview'))
  ]);
}

function renderReview(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  let reviewTo = '';
  if (m.toKey) {
    const [, refId] = m.toKey.split(':');
    const w = s.wallets.find((w) => w.id === refId);
    reviewTo = w ? w.name : '';
  }
  const isChainAsset = m.ccy !== s.reportingCurrency;
  // Reached only via the review step, i.e. after validateMove has already
  // confirmed m.amount is a strict decimal string — Number() here is exact.
  const amt = Number(m.amount || 0);
  // The chain a real broadcast actually settles on — same deductCcy
  // fallback as actions.js's buildTransferInputs (a reporting-currency
  // selection deducts from the source account's first real stablecoin
  // under the hood). See send.js's identical settlementChain comment.
  const settlementCcy = m.ccy === s.reportingCurrency ? Object.keys(curAccount.balances)[0] : m.ccy;
  const settlementChain = CONFIG.NET[settlementCcy] || '';

  const row = (label, value) => h('div', { class: 'review-row' }, [
    h('span', { class: 'k' }, label),
    h('span', { class: 'v' }, value)
  ]);
  // Real on-chain fee quote (feature-backend.md item 11) — replaces the old
  // amount*0.001 heuristic. Every real transfer costs gas now, so this is
  // unconditional, not gated on isChainAsset/cross-currency the way the
  // removed showFee was.
  // Not fmtNum() -- see send.js's identical comment on why (its fixed
  // 2-decimal-place formatting would round a typical native-gas fee down
  // to "0.00", hiding the fee CLAUDE.md requires be visible).
  // feature-outstanding.md (2026-08-07): a gasless quote's fee is
  // denominated in the transferred token itself (reviewFeeAsset ===
  // 'token'), never the chain's native currency — labeling it with
  // NATIVE_CCY would be wrong, not just imprecise, for those. m.reviewFeeError
  // is now shown verbatim rather than a generic "Unavailable" string: it's
  // either a real, actionable message actions.js already crafted (e.g. the
  // insufficient-balance-for-fee check) or the underlying quoteTransfer
  // failure's own message — both more useful on a signing-confirmation
  // screen than hiding the real reason.
  const feeCcy = settlementCcy;
  // Currency-conversion "deliver as" — mirrors send.js's identical
  // isSwapping condition, matching buildTransferInputs' own `swapping`
  // resolution exactly (actions.js) so the review screen can never show
  // the swap rows for a move that will actually sign as a plain transfer.
  const isSwapping = !!(m.deliverCcy && m.deliverCcy !== settlementCcy && swapDeliverAvailable(settlementChain));
  let feeValue;
  if (m.reviewFeeError) feeValue = m.reviewFeeError;
  else if (m.reviewFee != null) feeValue = m.reviewFee + ' ' + (m.reviewFeeAsset === 'token' ? feeCcy : (NATIVE_CCY[settlementChain] || ''));
  else feeValue = t('transfer.reviewFeeLoading');
  const feeRow = row(t('transfer.reviewFee'), feeValue);
  const gaslessNote = !m.reviewFeeError && m.reviewFee != null && m.reviewFeeAsset === 'token'
    ? h('div', { class: 'field-help' }, t('transfer.reviewFeeGasless', { native: NATIVE_CCY[settlementChain] || '', ccy: feeCcy }))
    : null;
  // Swap rows replace the plain fee row entirely when isSwapping — see
  // send.js's identical comment on why (fetchReviewFee's swapping branch
  // never touches reviewFee/reviewFeeError, so leaving feeRow in place
  // alongside these would show a permanent "loading" state).
  const swapFeeVal = swapFeeDisplay(m, feeCcy, NATIVE_CCY[settlementChain] || '') || t('transfer.reviewFeeLoading');
  const swapFeeRow = row(m.reviewSwapNeedsApproval ? t('transfer.reviewApprovalFee') : t('transfer.reviewFee'), swapFeeVal);
  const swapFeeGaslessNote = !m.reviewSwapError &&
    (m.reviewSwapNeedsApproval ? m.reviewSwapApproveFeeAsset : m.reviewSwapFeeAsset) === 'token'
    ? h('div', { class: 'field-help' }, t('transfer.reviewFeeGasless', { native: NATIVE_CCY[settlementChain] || '', ccy: feeCcy }))
    : null;
  const swapOutVal = m.reviewSwapError
    ? '—'
    : (m.reviewSwapOut != null ? fmtNum(Number(m.reviewSwapOut)) + ' ' + m.deliverCcy : t('transfer.reviewFeeLoading'));
  const swapOutRow = row(t('transfer.reviewRecipientReceives'), swapOutVal);
  const swapMultiStepNote = h('div', { class: 'field-help' }, t('transfer.swapMultiStepNote'));
  // CLAUDE.md: "mainnet actions need a visually distinct, explicit
  // confirmation step" — see send.js's identical mainnetGuard.
  const mainnetGuard = !s.testnetEnabled
    ? h('div', { class: 'mainnet-guard' }, [
        h('label', { class: 'checkbox-row mainnet-guard-row' }, [
          customCheckbox(m.mainnetAck, (e) => setMoveField('mainnetAck', e.target.checked)),
          t('transfer.mainnetAckLabel')
        ])
      ])
    : null;
  // Same swap-quote-resolved requirement as send.js's identical extension.
  const swapConfirmBlocked = m.reviewSwapLoading || !!m.reviewSwapError || m.reviewSwapOut == null ||
    (m.reviewSwapNeedsApproval ? m.reviewSwapApproveFee == null : m.reviewSwapFee == null);
  const confirmBlocked = m.sending || (!s.testnetEnabled && !m.mainnetAck) ||
    (isSwapping ? swapConfirmBlocked : (m.reviewFeeLoading || !!m.reviewFeeError || m.reviewFee == null));
  const limitOverridden = curAccount.sendLimit != null &&
    m.limitOverrideForAmount === m.amount && m.limitOverrideForCcy === m.ccy && m.limitOverrideForAccountId === m.fromAccountId;
  const limitRow = curAccount.sendLimit != null
    ? row(t('transfer.reviewSendLimit'), fmtReporting(curAccount.sendLimit) + (limitOverridden ? ' · ' + t('transfer.reviewLimitOverridden') : ''))
    : null;

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, t('transfer.reviewTitle')),
    h('div', { class: 'card stack' }, [
      row(t('transfer.reviewFrom'), curAccount.name + ' · ' + m.ccy),
      row(t('transfer.reviewTo'), reviewTo),
      row(t('transfer.reviewAmount'), fmtNum(amt) + ' ' + m.ccy),
      isChainAsset ? row(t('transfer.reviewChain'), m.chain) : null,
      isSwapping ? swapFeeRow : feeRow,
      isSwapping ? swapFeeGaslessNote : gaslessNote,
      isSwapping ? swapOutRow : null,
      isSwapping ? swapMultiStepNote : null,
      limitRow
    ]),
    mainnetGuard,
    m.errors.submit ? h('div', { class: 'field-error' }, m.errors.submit) : null,
    h('div', { class: 'btn-row' }, [
      h('button', { class: 'btn btn-secondary', disabled: m.sending, onclick: onMoveBack }, t('transfer.back')),
      h('button', {
        class: 'btn btn-primary btn-emphasis',
        disabled: confirmBlocked,
        onclick: confirmBlocked ? null : onMoveConfirm
      }, m.sending ? t('transfer.sending') : t('transfer.confirmTransfer'))
    ])
  ]);
}

function renderSuccess(s) {
  const tx = s.move.resultTx;
  // Real explorer link (feature-backend.md item 11) — see send.js's
  // identical comment. hashPending (GasFree/gasless-Tron async submission)
  // means tx.hash is a relay request id, not a real txHash yet — no link.
  const explorerHref = tx && !tx.hashPending ? explorerTxUrl(tx.chain, tx.hash, s.testnetEnabled) : null;
  // Currency-conversion "deliver as" outcome — see send.js's identical
  // resultTx.deliverCcy/tokenOutAmount handling.
  const swapOutcome = tx && tx.deliverCcy
    ? h('div', { class: 'success-sub' }, t('transfer.successSwapOutcome', { amount: fmtNum(Number(tx.tokenOutAmount || 0)) + ' ' + tx.deliverCcy }))
    : null;
  return h('div', { class: 'success-block' }, [
    h('div', { class: 'success-badge' }, h('div', { class: 'check' })),
    h('div', { class: 'success-title' }, t('transfer.successTitle')),
    h('div', { class: 'success-sub' }, t('transfer.successSub', { amount: tx ? fmtNum(tx.amount) + ' ' + tx.ccy : '' })),
    swapOutcome,
    h('div', { class: 'ref-card' }, [
      t('transfer.ref', { id: tx ? tx.id : '' }), h('br'),
      tx && tx.hashPending ? t('transfer.hashPending') : t('transfer.hash', { hash: tx ? tx.hash : '' })
    ]),
    explorerHref ? h('a', { href: explorerHref, target: '_blank', rel: 'noopener noreferrer' }, t('activity.viewOnChain')) : null,
    h('button', { class: 'btn btn-primary', onclick: onMoveDone }, t('transfer.done'))
  ]);
}

export function renderTransfer() {
  const s = getState();
  // No accounts yet (item 10 dropped the seeded demo wallets) — the
  // From-account lookups below all assume at least one exists.
  if (s.wallets.length === 0) {
    return h('div', { class: 'screen' }, [
      h('h1', { class: 'screen-title' }, t('transfer.title')),
      h('div', { class: 'empty-state' }, t('transfer.noAccountsEmpty')),
      h('button', { class: 'btn btn-primary', onclick: openOpenAccount }, t('transfer.openAccount'))
    ]);
  }
  const step = s.move.step;
  const inner = step === 'review' ? renderReview(s) : step === 'success' ? renderSuccess(s) : renderForm(s);
  // Desktop-only rail (see activityRail.js/style.css) — Transfer had no
  // recent-activity list at all pre-Desktop, unlike Home. Unlike Send
  // (renderSend.js), Transfer has no step-flow split-pane requirement of
  // its own, so its two-column layout is this generic content+rail one at
  // every step, not just 'form'.
  return h('div', { class: 'screen-layout' }, [
    h('div', { class: 'screen screen-main' }, [inner]),
    renderActivityRail(false)
  ]);
}
